import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { EmployeeLanguagesDto } from '@app/data/dtos/personal-data/employee-languages.dto';
import { LanguageMastersDto } from '@app/data/dtos/personal-data/language-masters.dto';
import { MaritalStatusDto } from '@app/data/dtos/personal-data/marital-status.dto';
import { PersonalDataResponseDto } from '@app/data/dtos/personal-data/personal-data/personal-data-response.dto';
import { PersonalDataDto } from '@app/data/dtos/personal-data/personal-data/personal-data.dto';
import { QualificationEducationalLevelsDto } from '@app/data/dtos/personal-data/qualification-educational-levels.dto';
import { QualificationDto } from '@app/data/dtos/personal-data/qualification.dto';
import { QualificationStatusesDto } from '@app/data/dtos/personal-data/qualifications-statuses.dto';
import { StudiesDto } from '@app/data/dtos/personal-data/studies.dto';
import {
  mapCertificateEmployeeDto,
  mapCreateQualificationDto,
  mapDataMaritalStatus,
  mapEmployeeLanguages,
  mapLanguageMasters,
  mapPersonalInfo,
  mapQualification,
  mapQualificationEducationalLevels,
  mapQualificationStatuses,
  mapStudies,
  mapUpdateAddress,
  mapUpdateEmergencyContact,
  mapUpdatePhones
} from '@app/data/mappers/personal-data/personal-data.mappers';
import { PersonalDataRepository } from '@app/domain/repositories/personal-data/personal-data.repository.contract';
import { Knowledge } from '@app/entities/interfaces/personal-data/employee-languages.interface';
import { ModifyStudy } from '@app/entities/interfaces/personal-data/studies.interface';
import { TwoFactorAuthorization } from '@app/entities/interfaces/ptrab/two-factor-authorization.interface';
import { PersonalDataAddress } from '@app/entities/models/personal-data/address.model';
import {
  CertificateEmployeeModel,
  EmployeeLanguages,
  LanguageMastersModel
} from '@app/entities/models/personal-data/employee-languages.model';
import { MaritalStatus } from '@app/entities/models/personal-data/marital-status.model';
import { PersonalData } from '@app/entities/models/personal-data/personal-data.model';
import {
  Qualification,
  QualificationEducationalLevels,
  QualificationStatuses,
  Studies,
  Study
} from '@app/entities/models/personal-data/studies.model';
import { UpdateEmergencyContact } from '@app/entities/models/personal-data/update-emergency-contact.model';
import { UpdatePhone } from '@app/entities/models/personal-data/update-phone.model';
import { ENV } from '@environment';
import { SECTION_KEY } from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MRepository } from '@mercadona/core/utils/repository';

@Injectable({ providedIn: 'root' })
export class PersonalDataRepositoryImpl extends MRepository implements PersonalDataRepository {
  private readonly PERSONAL_INFO_ENDPOINT = '/ptrab/personal-info';
  private readonly PERSONAL_INFO_EMAIL_ENDPOINT = '/ptrab/personal-info/email';
  private readonly PERSONAL_INFO_PHONES_ENDPOINT = '/ptrab/personal-info/phones';
  private readonly PERSONAL_INFO_EMERGENCY_ENDPOINT = '/ptrab/personal-info/emergency-phone';
  private readonly PERSONAL_INFO_ADDRESS_ENDPOINT = '/ptrab/personal-info/address';
  private employeeLanguages!: EmployeeLanguages;
  private studies!: Studies;

  constructor() {
    super(`${ENV.API_URL}`);
  }

  getPersonalData(): Observable<PersonalData> {
    return this.get<PersonalDataDto>(`${this.PERSONAL_INFO_ENDPOINT}`, {
      context: new HttpContext().set(SECTION_KEY, 'personal-data')
    }).pipe(map(mapPersonalInfo));
  }

  updateEmail(email: string, authorization: TwoFactorAuthorization): Observable<PersonalDataResponseDto> {
    return this.put(`${this.PERSONAL_INFO_EMAIL_ENDPOINT}`, {
      body: { email, authorization }
    });
  }

  createPersonalPhone(
    phoneData: UpdatePhone,
    authorization: TwoFactorAuthorization
  ): Observable<PersonalDataResponseDto> {
    const request = {
      body: {
        ...mapUpdatePhones(phoneData),
        authorization
      }
    };

    return this.post(`${this.PERSONAL_INFO_PHONES_ENDPOINT}`, request);
  }

  updatePersonalPhone(
    updatePhoneData: UpdatePhone,
    phoneId: string,
    authorization: TwoFactorAuthorization
  ): Observable<PersonalDataResponseDto> {
    const request = {
      body: {
        ...mapUpdatePhones(updatePhoneData),
        authorization
      }
    };

    return this.put(`${this.PERSONAL_INFO_PHONES_ENDPOINT}/${phoneId}`, request);
  }

  deletePhone(phoneSectionId: string): Observable<PersonalDataResponseDto> {
    return this.delete(`${this.PERSONAL_INFO_PHONES_ENDPOINT}/${phoneSectionId}`);
  }

  createEmergencyContact(emergencyContactData: UpdateEmergencyContact): Observable<PersonalDataResponseDto> {
    const request = {
      body: {
        ...mapUpdateEmergencyContact(emergencyContactData)
      }
    };

    return this.post(`${this.PERSONAL_INFO_EMERGENCY_ENDPOINT}`, request);
  }

  updateEmergencyContact(
    emergencyContactData: UpdateEmergencyContact,
    phoneId: string
  ): Observable<PersonalDataResponseDto> {
    const request = {
      body: {
        ...mapUpdateEmergencyContact(emergencyContactData)
      }
    };

    return this.put(`${this.PERSONAL_INFO_EMERGENCY_ENDPOINT}/${phoneId}`, request);
  }

  updateAddress(
    address: PersonalDataAddress,
    authorization: TwoFactorAuthorization
  ): Observable<PersonalDataResponseDto> {
    const request = {
      body: {
        address: mapUpdateAddress(address),
        authorization
      }
    };

    return this.put(`${this.PERSONAL_INFO_ADDRESS_ENDPOINT}`, request);
  }

  getMaritalStatusOptions(): Observable<MaritalStatus[]> {
    return this.get<MaritalStatusDto[]>(`/ptrab/civil-status`).pipe(map(mapDataMaritalStatus));
  }

  updateMaritalStatus(id: string, authorization: TwoFactorAuthorization): Observable<object> {
    const request = {
      body: {
        civil_status_id: id,
        authorization
      }
    };

    return this.put(`${this.PERSONAL_INFO_ENDPOINT}/civil-status`, request);
  }

  getEmployeeLanguages(): Observable<EmployeeLanguages> {
    return this.get<EmployeeLanguagesDto>(`/curriculums/employee/languages`, {
      context: new HttpContext().set(SECTION_KEY, 'personal-data-languages')
    }).pipe(
      map(mapEmployeeLanguages),
      tap((employeeLanguages: EmployeeLanguages) => {
        this.employeeLanguages = employeeLanguages;
      })
    );
  }

  getLanguagesMasters(): Observable<LanguageMastersModel> {
    return this.get<LanguageMastersDto>(`/curriculums/languages`).pipe(map(mapLanguageMasters));
  }

  getLanguagesData(): Knowledge[] | undefined {
    return this.employeeLanguages?.knowledge;
  }

  getStudiesData(): Study[] | undefined {
    return this.studies?.qualifications;
  }

  getEmployeeStudies(): Observable<Studies> {
    return this.get<StudiesDto>(`/qualifications/employee`, {
      context: new HttpContext().set(SECTION_KEY, 'personal-data-studies')
    }).pipe(
      map(mapStudies),
      tap((studies: Studies) => {
        this.studies = studies;
      })
    );
  }

  getQualifications(
    countryIsoNumericCode: string,
    educationalLevelId: string,
    page: number,
    name?: string
  ): Observable<Qualification> {
    let params = new HttpParams();
    params = params.set('country_iso_numeric_code', countryIsoNumericCode);
    params = params.set('educational_level_id', educationalLevelId);
    params = params.set('first_page', page);
    params = params.set('page_size', '50');
    if (name) {
      params = params.set('qualification_name', name);
    }

    return this.get<QualificationDto>(`/qualifications`, { params }).pipe(map(mapQualification));
  }

  getQualificationStatuses(): Observable<QualificationStatuses> {
    return this.get<QualificationStatusesDto>(`/qualifications/statuses`).pipe(map(mapQualificationStatuses));
  }

  getQualificationEducationalLevels(countryIsoNumericCode: string): Observable<QualificationEducationalLevels> {
    let params = new HttpParams();
    params = params.set('country_iso_numeric_code', countryIsoNumericCode);
    return this.get<QualificationEducationalLevelsDto>(`/qualifications/educational-levels`, { params }).pipe(
      map(mapQualificationEducationalLevels)
    );
  }

  createEmployeeCertificate(certificate: CertificateEmployeeModel): Observable<EmployeeLanguages> {
    const request = {
      body: mapCertificateEmployeeDto(certificate)
    };
    return this.post<EmployeeLanguagesDto>(`/curriculums/employee/languages`, request).pipe(map(mapEmployeeLanguages));
  }

  updateEmployeeCertificate(certificate: CertificateEmployeeModel): Observable<EmployeeLanguages> {
    const request = {
      body: mapCertificateEmployeeDto(certificate)
    };
    return this.put<EmployeeLanguagesDto>(`/curriculums/employee/languages/${certificate.language}`, request).pipe(
      map(mapEmployeeLanguages)
    );
  }

  addStudyToEmployee(qualification: ModifyStudy): Observable<object> {
    const request = {
      body: mapCreateQualificationDto(qualification)
    };
    return this.post<object>(`/qualifications/employee`, request);
  }

  updateEmployeeStudy(qualification: ModifyStudy): Observable<object> {
    const request = {
      body: mapCreateQualificationDto(qualification)
    };

    return this.put<object>(`/qualifications/employee`, request);
  }

  deleteEmployeeCertificate(languageId: string): Observable<object> {
    return this.delete(`/curriculums/employee/languages/${languageId}`);
  }

  deleteStudy(studyId: string, countryIsoNumericCode: string): Observable<object> {
    const request = {
      body: {
        qualification_id: studyId,
        country_iso_numeric_code: countryIsoNumericCode
      }
    };

    return this.delete(`/qualifications/employee`, request);
  }
}
